import React, { useEffect, useState } from 'react'
import { ArrayParam, NumberParam, useQueryParam } from 'use-query-params'
import { Icon } from '@iconify/react'
import { CheckboxChangeEvent } from 'antd/lib/checkbox'
import isEmpty from 'lodash.isempty'
import { Button, Dropdown, Checkbox, useUtils, Input } from '@pepper/ui'

import s from './VerticalFilter.module.css'

export const VerticalFilter = () => {
  const [open, setOpen] = useState(false)
  const [filteredVerticals, setFilteredVerticals] = useQueryParam(
    'vertical',
    ArrayParam
  )
  const [_, setCurrentPage] = useQueryParam('currentPage', NumberParam)
  const [selectedCategories, setSelectedCategories] = useState<
    (string | null)[]
  >([])
  const { verticals } = useUtils()
  const [matchingVerticals, setMatchingVerticals] = useState<
    VerticalObjectType[]
  >([])
  const [inputText, setInputText] = useState('')

  const isChecked = (id: string) => {
    return selectedCategories?.includes(id)
  }

  const handleApply = () => {
    setFilteredVerticals(selectedCategories)
    setCurrentPage(0)
    setOpen(false)
  }

  const handleReset = () => {
    setInputText('')
    setMatchingVerticals(verticals)
    setSelectedCategories([])
    setFilteredVerticals(undefined)
    setCurrentPage(0)
    setOpen(false)
  }

  const handleChange = (event: CheckboxChangeEvent) => {
    if (!event.target.name) return
    if (!isChecked(event.target.name)) {
      setSelectedCategories([...selectedCategories, event.target.name])
    } else {
      setSelectedCategories(
        selectedCategories.filter(category => category !== event.target.name)
      )
    }
  }

  const onVisibleChange = (visible: boolean) => {
    if (isEmpty(filteredVerticals)) setSelectedCategories([])
    setOpen(visible)
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(event.target.value)
    setMatchingVerticals(
      verticals.filter(vertical =>
        vertical.title.toLowerCase().includes(event.target.value.toLowerCase())
      )
    )
  }

  const normalizedVerticals: Record<string, VerticalObjectType> =
    verticals?.reduce(
      (prev, curr) => ({
        ...prev,
        [curr.id]: curr,
      }),
      {}
    )

  const appliedFilterText = (): string => {
    if (filteredVerticals?.length === 0 || !filteredVerticals) {
      return ''
    } else if (
      filteredVerticals?.length === 1 &&
      filteredVerticals[0] !== null
    ) {
      return normalizedVerticals[filteredVerticals?.[0]]?.title
    } else {
      return `${filteredVerticals?.length} selected`
    }
  }

  useEffect(() => {
    setMatchingVerticals(verticals)
  }, [verticals])

  useEffect(() => {
    setSelectedCategories(filteredVerticals ?? [])
  }, [filteredVerticals])

  const menu = () => {
    return (
      <div className={s.wrapper}>
        <Input
          onChange={handleInputChange}
          prefix={<Icon icon="lucide:search" className={s.searchIcon} />}
          placeholder="Search categories"
          className={s.inputBox}
          value={inputText}
        />
        <div className={s.options}>
          {matchingVerticals.length > 0 &&
            matchingVerticals.map(vertical => (
              <Checkbox
                key={vertical.id}
                checked={isChecked(vertical.id.toString())}
                name={vertical.id.toString()}
                className={s.option}
                onChange={handleChange}
              >
                <div className={s.label}>{vertical?.title}</div>
              </Checkbox>
            ))}
        </div>
        <div className={s.divider} />
        <div className={s.actions}>
          <Button className={s.actionBtn} type="primary" onClick={handleApply}>
            Apply
          </Button>
          <Button className={s.actionBtn} type="ghost" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>
    )
  }

  return (
    <Dropdown
      overlay={menu}
      trigger={['click']}
      visible={open}
      onVisibleChange={onVisibleChange}
    >
      <Button size="small" className={s.button}>
        Categories
        <div className={s.selected}>{appliedFilterText()}</div>
        <Icon
          icon="material-symbols:keyboard-arrow-down-rounded"
          width={16}
          height={16}
        />
      </Button>
    </Dropdown>
  )
}
